import * as React from "react";
import { AlertCircle } from "lucide-react";

import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { formStyles } from "@/lib/ui-styles";
import {
  getFieldWidthStyle,
  type FieldWidthSize,
} from "@/lib/form-field-width";

export interface InputProps extends React.ComponentProps<"input"> {
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  containerClassName?: string;
  /** Preset armónico min/max — default md */
  fieldSize?: FieldWidthSize;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      label,
      description,
      error,
      required = false,
      containerClassName,
      fieldSize = "md",
      id,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const fieldId = id ?? generatedId;
    const descriptionId = `${fieldId}-description`;
    const errorId = `${fieldId}-error`;

    const isFormField = !!(label || description || error);

    const inputElement = (
      <input
        ref={ref}
        id={fieldId}
        type={type}
        placeholder={placeholder}
        data-slot="input"
        aria-invalid={!!error}
        aria-describedby={
          error ? errorId : description ? descriptionId : undefined
        }
        className={cn(
          isFormField
            ? cn(
                formStyles.input,
                "w-full disabled:cursor-not-allowed disabled:text-muted-foreground disabled:opacity-50",
              )
            : "h-9 w-full min-w-0 rounded-md border border-input bg-white px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
          className,
        )}
        {...props}
      />
    );

    if (!isFormField) {
      return inputElement;
    }

    return (
      <div className={cn("space-y-1.5", containerClassName)}>
        {label && (
          <Label
            htmlFor={fieldId}
            className={cn(
              formStyles.label,
              "block",
              required &&
                "after:content-['*'] after:ml-0.5 after:text-destructive",
            )}
          >
            {label}
          </Label>
        )}
        <div
          className={formStyles.fieldControl}
          data-field-width={fieldSize}
          style={getFieldWidthStyle(placeholder, label, { size: fieldSize })}
        >
          {inputElement}
          {/* // ! Deshabilitado temporalmente: 
           {description && !error && (
            <p id={descriptionId} className={formStyles.fieldDescription}>
              {description}
            </p>
          )} */}
          <div className={formStyles.errorSlot}>
            {error && (
              <p id={errorId} role="alert" className={formStyles.fieldError}>
                <AlertCircle
                  className="w-3.5 h-3.5 shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  },
);

Input.displayName = "Input";

export { Input };
